import type { IUser, TRole } from '#interfaces/auth'
import { ROLE_ADMIN, ROLE_MANAGER, ROLE_SUPER_ADMIN, ROLE_USER } from '#interfaces/auth'

const KNOWN_ROLES: ReadonlySet<string> = new Set<string>([ROLE_USER, ROLE_MANAGER, ROLE_ADMIN, ROLE_SUPER_ADMIN])

/**
 * Decode a JWT payload without verifying the signature.
 * JWTs are base64url-encoded — no secret is needed to read the claims.
 */
function decodePayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null

    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const json = atob(base64)

    const parsed: unknown = JSON.parse(json)
    if (typeof parsed !== 'object' || parsed === null) return null
    return parsed as Record<string, unknown>
  } catch {
    return null
  }
}

/**
 * Get the expiration timestamp (in ms) from a JWT.
 * Returns null if the token is malformed or has no `exp` claim.
 */
export function getTokenExpirationMs(token: string): number | null {
  const payload = decodePayload(token)
  if (!payload || typeof payload.exp !== 'number') return null

  return payload.exp * 1000
}

/**
 * Calculate when to schedule a proactive refresh.
 * Returns the delay in ms, or null if the token can't be decoded.
 *
 * Strategy: refresh at 80% of the token's remaining lifetime,
 * but always at least 60 seconds before expiry.
 */
export function getRefreshDelay(token: string): number | null {
  const expiresAtMs = getTokenExpirationMs(token)
  if (expiresAtMs === null) return null

  const now = Date.now()
  const remainingMs = expiresAtMs - now

  if (remainingMs <= 0) return 0

  const MINIMUM_BUFFER_MS = 60_000
  const refreshAt = Math.min(remainingMs * 0.8, remainingMs - MINIMUM_BUFFER_MS)

  return Math.max(refreshAt, 0)
}

/**
 * Extract user info from a Keycloak JWT access token.
 * Returns null if the token is malformed or missing required claims.
 *
 * This provides instant user hydration without an API call.
 * The /auth/me endpoint should still be called in the background
 * for server-verified fields like `isImpersonating`, `enabled`, `locked`.
 */
export function decodeUserFromToken(token: string): IUser | null {
  const payload = decodePayload(token)
  if (!payload) return null

  const sub = payload.sub
  const email = payload.email
  if (typeof sub !== 'string' || typeof email !== 'string') return null

  const firstName = typeof payload.given_name === 'string' ? payload.given_name : undefined
  const lastName = typeof payload.family_name === 'string' ? payload.family_name : undefined
  const displayName = typeof payload.name === 'string' ? payload.name : undefined

  // Extract roles from Keycloak's realm_access.roles array
  const realmAccess = payload.realm_access
  let rawRoles: unknown[] = []
  if (typeof realmAccess === 'object' && realmAccess !== null && 'roles' in realmAccess) {
    const rolesValue = (realmAccess as { roles: unknown }).roles
    if (Array.isArray(rolesValue)) rawRoles = rolesValue
  }

  const roles = rawRoles.filter((r): r is TRole => typeof r === 'string' && KNOWN_ROLES.has(r))

  return {
    id: sub,
    email,
    firstName,
    lastName,
    displayName,
    roles,
    locale: 'nl',
    enabled: true,
    locked: false,
  }
}
